import React from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { SpinUuidLookup } from '../SpinPage/SpinUuidLookup.js'
import { tsLabel } from './spinMetrics.logic.js'
import type { useSelectedRun } from './useSelectedRun.js'

type Selection = ReturnType<typeof useSelectedRun>

const NAV: Array<{ seg: string; label: string }> = [
  { seg: '', label: 'Overview' },
  { seg: 'cohesion', label: 'Cohesion' },
  { seg: 'compare', label: 'Compare' },
  { seg: 'reference', label: 'Reference' },
  { seg: 'singletons', label: 'Singletons' },
  { seg: 'leaderboard', label: 'Top SPINs' },
  { seg: 'growth', label: 'Growth' },
  { seg: 'drift', label: 'Drift' },
  { seg: 'flow', label: 'Flow' },
  { seg: 'export', label: 'Export' },
  { seg: 'about', label: 'Definitions' }
]

/** Tab-style nav across the SPIN pages. Every link carries the current query string so the selected
 *  run/taxonomy persists across the whole section. */
export const SpinNav: React.FC = () => {
  const [params] = useSearchParams()
  const { pathname } = useLocation()
  const qs = params.toString()
  const suffix = qs ? `?${qs}` : ''
  return (
    <nav className="flex flex-wrap gap-1 border-b border-gray-200">
      {NAV.map(({ seg, label }) => {
        const to = `/admin/spin${seg ? `/${seg}` : ''}`
        const active = pathname === to
        return (
          <Link
            key={seg || 'overview'}
            to={`${to}${suffix}`}
            className={`px-3 py-1.5 text-sm rounded-t ${
              active ? 'bg-base-200 font-semibold border-b-2 border-primary' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {label}
          </Link>
        )
      })}
    </nav>
  )
}

/** Shared header for every SPIN page: title + taxonomy/run selectors (bound to the URL via
 *  useSelectedRun) + the section nav + run-level loading/empty notices. */
export const SpinChrome: React.FC<{ title: string; sel: Selection }> = ({ title, sel }) => {
  const { runs, selected, tax, amplicons, runOptions, setRun, setTax, loading, error } = sel
  const [params] = useSearchParams()
  const qs = params.toString()
  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">{title}</h1>
        <div className="flex flex-wrap items-center gap-4">
          {amplicons.length > 0 && (
            <label className="flex items-center gap-2 text-sm">
              Taxonomy
              <select
                className="select select-bordered select-sm"
                value={tax}
                onChange={(e) => setTax(e.target.value)}
                disabled={loading}
              >
                <option value="">All</option>
                {amplicons.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            Run
            <select
              className="select select-bordered select-sm"
              value={selected?.id ?? ''}
              onChange={(e) => setRun(e.target.value)}
              disabled={loading || runs.length === 0}
            >
              {runOptions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.kind} · {r.algorithmType} · {r.amplicon ?? 'all'} · {tsLabel(r.startedAt)} ({r.spinCount ?? '?'})
                </option>
              ))}
            </select>
          </label>
          <SpinUuidLookup toPath={(id) => `/admin/spin/s/${id}${qs ? `?${qs}` : ''}`} />
        </div>
      </header>

      <SpinNav />

      {loading && <p className="text-gray-500">Loading runs…</p>}
      {error && <p className="text-error">Could not load runs: {error}</p>}
      {!loading && !error && runs.length === 0 && (
        <p className="text-gray-500">No SPIN runs found — run the clustering algorithm first.</p>
      )}
    </div>
  )
}
